import { ChartProps } from '../../Charts'
import React, { useEffect, useMemo, useRef, useState } from 'react'
import StackedBarChart from '../../charts/StackedBarChart/StackedBarChart'
import LineChart from '../../charts/LineChart/LineChart'
import LineGradientChart from '../../charts/LineGradientChart'
import XYChart from '../../charts/XYChart/XYChart'
import MultiAxisChart from '../../charts/MultiAxis/MultiAxisChart'
import PieChart from '../../charts/PieChart'
import DifferenceChart from '../../charts/DifferenceChart'
import Dropdown from '../../../Inputs/Dropdown/Dropdown'
import Popup from '../../../Popup/Popup'
import Button from '../../../Button/Button'
import Row from '../../../Row/Row'
import Column from '../../../Column/Column'
import Checkbox from '../../../Inputs/Checkbox/Checkbox'
import Visible from '../../../Visible/Visible'
import Title from '../../../Text/Title/Title'
import Modal from '../../../Modal/Modal'

const chartTypeMap = [
    { name: 'Difference', value: 'Difference' },
    { name: 'Area', value: 'AreaChart' },
    { name: 'Stacked Area', value: 'StackedArea' },
    { name: 'Stacked Bar', value: 'StackedBar' },
    { name: 'Bar Group', value: 'BarGroup' },
    { name: 'Bar', value: 'BarChart' },
    { name: 'Line', value: 'LineChart' },
    { name: 'Line Gradient', value: 'LineGradientChart' },
    { name: 'Pie', value: 'Pie' },
]

const Chart = () => {}

const Wrapper: React.FC<ChartProps> = (props) => {
    const lsKey = '__chart_config__'
    const {
        ylabel,
        xlabel,
        ylabelOffset,
        xlabelOffset,
        name,
        margin,
        tickFormat,
        chartTypes,
        valueDisplayFormat,
        valueKeys,
        xAxisKey,
        width,
        height,
        data,
        xAxisScope,
        orientation,
        fixedChartType,
        defaultChartType,
        fixedLegend,
        hideMenu,
        hideLabel,
        hideLegend,
        showPieCount,
        legendPosition,
        onDataItemClick,
        title,
        titleContainer,
        pieDiameter,
        showPieCountOnFocus,
        pieCalloutTextColor,
        secondaryValueKeys,
        colorAccessor,
        tooltipRenderer,
    } = props

    const initial = useMemo(() => {
        const config = localStorage.getItem(lsKey)
        const parsed = JSON.parse(config) || {}

        return parsed[name] || 'StackedArea'
    }, [name])

    const [selectedChartType, setSelectedChartType] = useState(fixedChartType || defaultChartType || initial)
    const [showMenu, setShowMenu] = useState(false)
    const [showFullScreen, setShowFullScreen] = useState(false)
    const [isReversed, setIsReversed] = useState(false)
    const [compAKey, setCompAKey] = useState(valueKeys.length > 0 ? valueKeys[0].key : null)
    const [compBKey, setCompBKey] = useState(valueKeys.length > 1 ? valueKeys[1].key : null)
    const [legend, setLegend] = useState(null)
    const menuBtnRef = useRef(null)

    useEffect(() => {
        const config = localStorage.getItem(lsKey)
        const parsed = JSON.parse(config) || {}

        parsed[name] = selectedChartType

        localStorage.setItem(lsKey, JSON.stringify(parsed))
    }, [selectedChartType])

    // const [selectedChartType, setSelectedChartType] = useState(chartTypes[0])
    const ref = useRef()
    const renderChart = useMemo(() => {
        let _data = isReversed ? data.slice().reverse() : data.slice()

        setLegend(null)

        let _width = showFullScreen ? window.innerWidth - 280 : width

        if (fixedLegend && legendPosition !== 'BOTTOM') {
            _width -= 200
        }

        const props = {
            margin,
            data: _data,
            width: _width,
            height: showFullScreen ? window.innerHeight - 100 : height,
            xAxisKey,
            valueKeys,
            setLegend,
            tickFormat,
            valueDisplayFormat,
            ylabel,
            xlabel,
            ylabelOffset,
            xlabelOffset,
            xAxisScope,
            orientation,
            legendPosition,
            onDataItemClick,
            secondaryValueKeys,
            colorAccessor,
            tooltipRenderer,
        }
        console.log('PROPS', props)
        switch (selectedChartType) {
            case 'StackedBar':
                return <XYChart {...props} type={selectedChartType} />
            case 'Pie':
                return (
                    <PieChart
                        {...props}
                        pieCalloutTextColor={pieCalloutTextColor}
                        showPieCountOnFocus={showPieCountOnFocus}
                        pieDiameter={pieDiameter}
                        showPieCount={showPieCount}
                        hideLabel={hideLabel}
                    />
                )
            case 'LineChart':
                return <XYChart {...props} type={selectedChartType} />
            case 'AreaChart':
                return <XYChart {...props} type={selectedChartType} />
            case 'StackedArea':
                return <XYChart {...props} type={selectedChartType} />
            case 'BarGroup':
                return <XYChart {...props} type={selectedChartType} />
            // case 'MultiAxis':
            //     return <MultiAxisChart {...props} type={selectedChartType} />
            case 'BarChart':
                return <XYChart {...props} type={selectedChartType} />
            case 'Difference':
                return <DifferenceChart compAKey={compAKey} compBKey={compBKey} {...props} />
            default:
                return <div>No chart found</div>
        }
    }, [width, showFullScreen, isReversed, data, compAKey, compBKey, valueKeys, selectedChartType])

    const availChartType = useMemo(() => {
        if (!chartTypes) {
            return chartTypeMap
        }
        return chartTypeMap.filter((c) => chartTypes.indexOf(c.value) !== -1)
    }, [chartTypes])

    const chart = (
        <div
            style={{ position: 'relative' }}
            className={`r-chart-container r-chart-legend-pos-${legendPosition || 'RIGHT'} ${
                showFullScreen ? 'r-state-fullscreen' : ''
            }`}
        >
            <div className={'r-chart-header'}>
                {titleContainer || <div className={'r-chart-title'}>{title}</div>}
                <div className={'r-chart-header-legend'}>
                    <Visible visible={selectedChartType === 'Difference'}>
                        <div style={{ display: 'flex', maxWidth: '700px' }}>
                            <div style={{ marginRight: '20px' }}>
                                <Dropdown
                                    label={{ pos: 'left', text: 'Compare: ' }}
                                    value={compAKey}
                                    onChange={setCompAKey}
                                    textField={'label'}
                                    valueField={'key'}
                                    dataSource={valueKeys}
                                    name={'compA'}
                                />
                                <div style={{ marginTop: '8px', width: '100%', borderBottom: '2px solid black' }} />
                            </div>
                            <div>
                                <Dropdown
                                    label={{ pos: 'left', text: 'To: ' }}
                                    value={compBKey}
                                    onChange={setCompBKey}
                                    textField={'label'}
                                    valueField={'key'}
                                    dataSource={valueKeys}
                                    name={'compB'}
                                />
                                <div style={{ marginTop: '8px', width: '100%', borderBottom: '2px dashed black' }} />
                            </div>
                        </div>
                    </Visible>
                    <Visible visible={!hideLegend && selectedChartType !== 'Difference'}>{legend}</Visible>
                    <Visible visible={!hideMenu}>
                        <Button
                            onClick={() => setShowMenu(!showMenu)}
                            ref={menuBtnRef}
                            style={{ marginLeft: '20px' }}
                            isDiscreet
                            icon={'caret-square-down'}
                        />
                    </Visible>
                </div>
            </div>
            {renderChart}
            <Popup show={showMenu} anchor={menuBtnRef}>
                <Row>
                    <Column>
                        <div className={'r-chart-menu-open'} onClick={() => setShowFullScreen(true)}>
                            Expand
                        </div>
                    </Column>
                    <Visible visible={true}>
                        <Column>
                            <Dropdown
                                value={selectedChartType}
                                onChange={(v) => {
                                    setSelectedChartType(v)
                                    // setShowMenu(false)
                                }}
                                textField={'name'}
                                valueField={'value'}
                                dataSource={availChartType}
                                name={'chartType'}
                            />
                        </Column>
                    </Visible>
                </Row>
            </Popup>
            <Visible visible={fixedLegend}>
                <div className={`r-chart-legend`}>
                    <Title>Legend</Title>
                </div>
            </Visible>
            {/*<Visible visible={!hideMenu}>*/}
            {/*    <div className={`r-chart-sidenav ${showMenu || showFullScreen ? 'r-state-open' : ''}`}>*/}
            {/*        <Visible visible={!showMenu && !showFullScreen}>*/}
            {/*            <div className={'r-chart-menu-buttons'}>*/}
            {/*                <div className={'r-chart-menu-open'} onClick={() => setShowFullScreen(true)}>*/}
            {/*                    Expand*/}
            {/*                </div>*/}
            {/*                <div className={'r-chart-menu-open'} onClick={() => setShowMenu(true)}>*/}
            {/*                    Menu*/}
            {/*                </div>*/}
            {/*            </div>*/}
            {/*        </Visible>*/}
            {/*        <Visible visible={showMenu || showFullScreen}>*/}
            {/*            <Visible visible={!showFullScreen}>*/}
            {/*                <Button*/}
            {/*                    style={{*/}
            {/*                        position: 'absolute',*/}
            {/*                        top: 0,*/}
            {/*                        right: 0,*/}
            {/*                    }}*/}
            {/*                    isDiscreet*/}
            {/*                    size={'sm'}*/}
            {/*                    type={'danger'}*/}
            {/*                    icon={'window-close'}*/}
            {/*                    onClick={() => setShowMenu(false)}*/}
            {/*                />*/}
            {/*            </Visible>*/}
            {/*            <Row>*/}
            {/*                <Visible visible={!fixedChartType}>*/}
            {/*                    <Column>*/}
            {/*                        <Dropdown*/}
            {/*                            value={selectedChartType}*/}
            {/*                            onChange={(v) => {*/}
            {/*                                setSelectedChartType(v)*/}
            {/*                                // setShowMenu(false)*/}
            {/*                            }}*/}
            {/*                            textField={'name'}*/}
            {/*                            valueField={'value'}*/}
            {/*                            dataSource={availChartType}*/}
            {/*                            name={'chartType'}*/}
            {/*                        />*/}
            {/*                    </Column>*/}
            {/*                </Visible>*/}
            {/*                <Visible visible={selectedChartType === 'Difference'}>*/}
            {/*                    <Column>*/}
            {/*                        <Dropdown*/}
            {/*                            label={'Compare: '}*/}
            {/*                            value={compAKey}*/}
            {/*                            onChange={setCompAKey}*/}
            {/*                            textField={'label'}*/}
            {/*                            valueField={'key'}*/}
            {/*                            dataSource={valueKeys}*/}
            {/*                            name={'compA'}*/}
            {/*                        />*/}
            {/*                        <div style={{ marginTop: '8px', width: '100%', borderBottom: '2px solid black' }} />*/}
            {/*                    </Column>*/}
            {/*                    <Column>*/}
            {/*                        <Dropdown*/}
            {/*                            label={'To: '}*/}
            {/*                            value={compBKey}*/}
            {/*                            onChange={setCompBKey}*/}
            {/*                            textField={'label'}*/}
            {/*                            valueField={'key'}*/}
            {/*                            dataSource={valueKeys}*/}
            {/*                            name={'compB'}*/}
            {/*                        />*/}
            {/*                        <div*/}
            {/*                            style={{ marginTop: '8px', width: '100%', borderBottom: '2px dashed black' }}*/}
            {/*                        />*/}
            {/*                    </Column>*/}
            {/*                </Visible>*/}
            {/*                <Visible*/}
            {/*                    visible={selectedChartType === 'Difference' && Boolean(compBKey) && Boolean(compAKey)}*/}
            {/*                >*/}
            {/*                    <Column>*/}
            {/*                        <div>*/}
            {/*                            This chart shows the comparison between two values. When{' '}*/}
            {/*                            {valueKeys.find((v) => v.key === compAKey)?.label} is greater than{' '}*/}
            {/*                            {valueKeys.find((v) => v.key === compBKey)?.label} the difference is show in{' '}*/}
            {/*                            <span style={{ color: '#00a98a' }}>green</span>. When{' '}*/}
            {/*                            {valueKeys.find((v) => v.key === compBKey)?.label} is greater than{' '}*/}
            {/*                            {valueKeys.find((v) => v.key === compAKey)?.label} the difference is show in{' '}*/}
            {/*                            <span style={{ color: '#ff8282' }}>red</span>.*/}
            {/*                        </div>*/}
            {/*                    </Column>*/}
            {/*                </Visible>*/}
            {/*                <Visible visible={selectedChartType !== 'Difference'}>*/}
            {/*                    <Column>*/}
            {/*                        <Checkbox*/}
            {/*                            label={{ pos: 'right', text: 'Reverse Data' }}*/}
            {/*                            name={'reverse'}*/}
            {/*                            onChange={setIsReversed}*/}
            {/*                            value={isReversed}*/}
            {/*                        />*/}
            {/*                    </Column>*/}
            {/*                </Visible>*/}
            {/*                <Visible visible={legend}>*/}
            {/*                    <Column>*/}
            {/*                        <Title>Legend</Title>*/}
            {/*                        {legend}*/}
            {/*                    </Column>*/}
            {/*                </Visible>*/}
            {/*            </Row>*/}
            {/*        </Visible>*/}
            {/*    </div>*/}
            {/*</Visible>*/}

            {/*<Popup width={'240px'} anchor={ref} show={showMenu}>*/}
            {/*    <Row>*/}
            {/*        <Column>*/}

            {/*        </Column>*/}
            {/*    </Row>*/}
            {/*</Popup>*/}
        </div>
    )

    return showFullScreen ? (
        <Modal onClose={() => setShowFullScreen(false)} show height={'90vh'} width={'98vw'}>
            {chart}
        </Modal>
    ) : (
        chart
    )
}

export default Wrapper
